import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  FiDollarSign, FiTrendingUp, FiBriefcase, FiDownload, 
  FiSearch, FiCalendar 
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function FinancePage() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // ฟิลเตอร์
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetch(`${API_URL}?action=get_all_jobs`)
      .then(res => res.json())
      .then(data => {
        // กรองเฉพาะงานที่สำเร็จ (completed) สำหรับการคำนวณการเงิน
        const allJobs = data.data || [];
        setJobs(allJobs);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setIsLoading(false);
        Swal.fire('ข้อผิดพลาด', 'ไม่สามารถดึงข้อมูลการเงินได้', 'error');
      });
  }, []);

  const completedJobs = jobs.filter(j => j.status === 'completed');

  // กรองงานตามวันที่และคำค้นหา
  const filteredJobs = completedJobs.filter(job => {
    const jobDate = new Date(job.createdAt);
    const isAfterStart = startDate ? jobDate >= new Date(startDate) : true;
    const isBeforeEnd = endDate ? jobDate <= new Date(endDate + 'T23:59:59') : true;
    
    const safeCustomerName = job.customerName || '';
    const safePhone = job.phone || '';
    const safeProblem = job.problem || '';
    const isSearchMatch = 
      safeCustomerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      safePhone.includes(searchTerm) ||
      safeProblem.toLowerCase().includes(searchTerm.toLowerCase());
    
    return isAfterStart && isBeforeEnd && isSearchMatch;
  });

  // คำนวณสรุปสถิติ
  const totalRevenue = filteredJobs.reduce((sum, j) => sum + (Number(j.price) || 0), 0);
  const totalJobsCount = filteredJobs.length;
  const averageTicket = totalJobsCount > 0 ? Math.round(totalRevenue / totalJobsCount) : 0;

  // กราฟรายเดือน (ม.ค. - ธ.ค.)
  const thMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  const monthlyRevenue = thMonths.map(month => ({ name: month, รายได้: 0, งาน: 0 }));

  filteredJobs.forEach(job => {
    if (job.createdAt) {
      const date = new Date(job.createdAt);
      const monthIdx = date.getMonth();
      monthlyRevenue[monthIdx].รายได้ += Number(job.price) || 0;
      monthlyRevenue[monthIdx].งาน += 1;
    }
  });

  // สัดส่วนประเภทงาน (Pie Chart)
  const getJobTypeLabel = (type) => {
    const types = { 
      'aircon': 'แอร์', 
      'electric': 'ไฟฟ้า', 
      'contractor': 'รับเหมา', 
      'extension': 'ต่อเติม', 
      'solar': 'โซล่าเซลล์', 
      'door': 'ประตู', 
      'evertor': 'ลิฟท์', 
      'camera': 'กล้อง',
      'rework': 'งานแก้' 
    };
    return types[type] || 'ทั่วไป';
  };

  const jobTypeStats = {};
  filteredJobs.forEach(job => {
    const typeLabel = getJobTypeLabel(job.job_type);
    const price = Number(job.price) || 0;
    if (!jobTypeStats[typeLabel]) {
      jobTypeStats[typeLabel] = { name: typeLabel, value: 0, count: 0 };
    }
    jobTypeStats[typeLabel].value += price;
    jobTypeStats[typeLabel].count += 1;
  });

  const pieData = Object.values(jobTypeStats).sort((a, b) => b.value - a.value);

  // สีสำหรับ Pie Chart
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b'];

  // ฟังก์ชันดาวน์โหลดรายงานการเงินเป็น Excel (CSV)
  const exportFinanceToCSV = () => {
    if (filteredJobs.length === 0) {
      return Swal.fire('แจ้งเตือน', 'ไม่มีข้อมูลสำหรับดาวน์โหลดในวันที่เลือก', 'warning');
    }
    const headers = ['วันที่ทำรายการ', 'ชื่อลูกค้า', 'เบอร์โทรศัพท์', 'ประเภทงาน', 'รายละเอียดงานซ่อม', 'จำนวนเงิน (บาท)'];
    const csvRows = filteredJobs.map(job => [
      new Date(job.createdAt).toLocaleDateString('th-TH'),
      job.customerName || '-',
      job.phone || '-',
      getJobTypeLabel(job.job_type),
      `"${(job.problem || '').replace(/"/g, '""')}"`,
      job.price || '0'
    ].join(','));
    
    const csvContent = "\uFEFF" + [headers.join(','), ...csvRows].join('\n'); // Add BOM สำหรับภาษาไทย
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `รายงานการเงิน_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-gray-100">
      
      {/* ส่วนหัว */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black flex items-center gap-2">
            💰 สรุปรายได้และการเงิน
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            วิเคราะห์ภาพรวมการเงิน ยอดรายรับ และสถิติแยกประเภทบริการ
          </p>
        </div>
        <button 
          onClick={exportFinanceToCSV}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition"
        >
          <FiDownload size={14} /> Export รายงานการเงิน
        </button>
      </div>

      {/* สรุปสถิติ (Metric Cards) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm flex items-center gap-5 transition-colors">
          <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
            <FiDollarSign size={28} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">รายได้ทั้งหมด</p>
            <h3 className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
              ฿{totalRevenue.toLocaleString()}
            </h3>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm flex items-center gap-5 transition-colors">
          <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
            <FiBriefcase size={28} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">จำนวนงานซ่อมที่ปิด</p>
            <h3 className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
              {totalJobsCount} งาน
            </h3>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm flex items-center gap-5 transition-colors">
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
            <FiTrendingUp size={28} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">ค่าเฉลี่ยต่อบิล (Ticket Size)</p>
            <h3 className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
              ฿{averageTicket.toLocaleString()}
            </h3>
          </div>
        </div>
      </div>

      {/* แถบฟิลเตอร์ */}
      <div className="bg-white dark:bg-[#1e293b] p-4 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm flex flex-col md:flex-row gap-4 transition-colors">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-3 text-gray-400" />
          <input 
            type="text" 
            placeholder="ค้นหาชื่อลูกค้า, เบอร์โทรศัพท์, อาการ..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-sm dark:text-white outline-none focus:border-blue-500 transition-colors" 
          />
        </div>
        <div className="flex items-center gap-2 md:w-96">
          <FiCalendar className="text-gray-400 shrink-0" />
          <input 
            type="date" 
            value={startDate} 
            onChange={e => setStartDate(e.target.value)} 
            className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-sm dark:text-white outline-none transition-colors" 
            title="ตั้งแต่" 
          />
          <span className="text-gray-400 font-bold">-</span>
          <input 
            type="date" 
            value={endDate} 
            onChange={e => setEndDate(e.target.value)} 
            className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-sm dark:text-white outline-none transition-colors" 
            title="ถึง" 
          />
        </div>
      </div>

      {/* กราฟวิเคราะห์ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* กราฟแท่ง รายรับรายเดือน */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm transition-colors">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-300 mb-4">สถิติรายได้แยกตามเดือนในปีนี้ (บาท)</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{fontSize: 11, fill: '#94a3b8'}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize: 11, fill: '#94a3b8'}} axisLine={false} tickLine={false} width={45} />
                <Tooltip 
                  cursor={{fill: '#f1f5f9'}} 
                  contentStyle={{
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: '#1e293b',
                    color: '#fff'
                  }} 
                />
                <Bar dataKey="รายได้" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* กราฟวงกลม สัดส่วนประเภทงาน */}
        <div className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm transition-colors">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-300 mb-4">สัดส่วนรายได้ตามประเภทงาน</h3>
          <div className="h-64 w-full flex justify-center items-center relative">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `฿${value.toLocaleString()}`} />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 10 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-xs">ไม่มีข้อมูลสรุปงาน</p>
            )}
          </div>
        </div>

      </div>

      {/* ตารางรายการเงินที่ปิดงาน */}
      <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
        <div className="p-4 bg-gray-50 dark:bg-[#0f172a]/50 border-b border-gray-200 dark:border-[#334155] flex justify-between items-center transition-colors">
          <span className="text-xs font-bold text-gray-500">
            รายการปิดงาน: <span className="text-blue-600">{filteredJobs.length}</span> รายการ
          </span>
          <span className="text-xs font-black text-green-600 dark:text-green-400">
            ยอดรวมฟิลเตอร์: ฿{totalRevenue.toLocaleString()}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 text-xs font-bold uppercase border-b border-gray-200 dark:border-[#334155] transition-colors">
                <th className="p-4 pl-6">วันที่ทำรายการ</th>
                <th className="p-4">ชื่อลูกค้า</th>
                <th className="p-4">ประเภทงาน</th>
                <th className="p-4">รายละเอียดงาน</th>
                <th className="p-4 text-right">จำนวนเงิน</th>
                <th className="p-4 pr-6 text-center">สถานะ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="p-10 text-center text-blue-500 font-bold">กำลังโหลด...</td>
                </tr>
              ) : filteredJobs.length > 0 ? (
                filteredJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-[#131825]/50 transition-colors">
                    <td className="p-4 pl-6 text-xs text-gray-500">
                      {new Date(job.createdAt).toLocaleDateString('th-TH')} {new Date(job.createdAt).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="p-4 font-bold text-gray-800 dark:text-white">
                      {job.customerName || '-'}
                      <p className="text-[10px] text-gray-400 font-normal">📞 {job.phone || '-'}</p>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-[10px] font-bold">
                        {getJobTypeLabel(job.job_type)}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-gray-600 dark:text-gray-300 max-w-xs truncate" title={job.problem}>
                      {job.problem || '-'}
                    </td>
                    <td className="p-4 text-right font-black text-green-600 dark:text-green-400">
                      ฿{Number(job.price).toLocaleString()}
                    </td>
                    <td className="p-4 pr-6 text-center">
                      <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 text-[10px] font-bold">
                        เสร็จสิ้น
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-10 text-center text-gray-500 font-bold">ไม่พบข้อมูลการปิดเงินในช่วงที่เลือก</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
    </div>
  );
}
